import type { LucideIcon } from 'lucide-preact'
import type { ComponentChildren } from 'preact'
import { useEffect, useRef } from 'preact/hooks'
import type { UiSoundId } from '#contract'
import { useClient } from '../app/client-context.ts'
import styles from './button.module.css'

interface ButtonProps {
  readonly children: ComponentChildren
  readonly onClick: () => void
  /** `danger` confirms something that can't be undone (deleting a save). */
  readonly variant?: 'primary' | 'ghost' | 'danger'
  /** A second, smaller line under the label: the save Continue loads. */
  readonly detail?: ComponentChildren
  readonly icon?: LucideIcon
  /** Focuses the button on mount, so keyboard and gamepad users start there. */
  readonly autoFocus?: boolean
  /** Played on activation. Back screens pass `back`. */
  readonly sound?: 'click' | 'back'
}

/** One hover chirp per gap, shared by every button. */
const HOVER_GAP_MS = 80
let lastHover = 0

export function Button({
  children,
  onClick,
  variant = 'primary',
  icon: Icon,
  detail,
  autoFocus = false,
  sound = 'click',
}: ButtonProps) {
  const ref = useRef<HTMLButtonElement>(null)
  const client = useClient()

  // The `autofocus` attribute is ignored for elements inserted after page load.
  useEffect(() => {
    if (autoFocus) ref.current?.focus()
  }, [autoFocus])

  const play = (id: UiSoundId): void => {
    client?.playUiSound(id)
  }

  return (
    <button
      ref={ref}
      type="button"
      class={[styles.button, styles[variant]].join(' ')}
      onClick={() => {
        onClick()
        play(sound)
      }}
      onPointerEnter={(event) => {
        if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return
        const now = performance.now()
        if (now - lastHover < HOVER_GAP_MS) return
        lastHover = now
        play('hover')
      }}
    >
      {Icon && <Icon class={styles.icon} aria-hidden="true" />}
      {detail === undefined ? (
        <span>{children}</span>
      ) : (
        <span class={styles.lines}>
          <span>{children}</span>
          <span class={styles.detail}>{detail}</span>
        </span>
      )}
    </button>
  )
}
